import {
	action,
	computed,
	makeObservable,
	observable,
	runInAction
} from 'mobx';
import { lmsAxiosClient } from 'LearningManager/service/lmsClient';
import { throttledRequest } from 'LearningManager/helpers/concurrency';
import LEARNING_MANAGER_API from 'Services/Api/LearningManager/Api';
import {
	LearningPath,
	LearningPathIdResponse,
	LearningPathPayload,
	ListLearningPathsParams,
	LearningPathSetupType,
	ListLearningPathsResponse,
	LearningPathDetailResponse
} from 'Services/Api/LearningManager/Types';
import { Course } from 'Services/Api/Courses/Types';
import ASSIGNMENT_SOURCES_API from 'Services/Api/AssignmentSources/Api';
import {
	AssignmentSource,
	AssignmentSourcePayload,
	AssignmentSourceUpdatePayload,
	AssignmentSourceIdResponse,
	AssignmentSourceDetailResponse,
	AssignmentSourceLimitedUpdatePayload,
	AssignmentSourceUsersPayload,
	AssignmentScopeEntity,
	AssignmentScopeUserEntity,
	AssignmentScopeGroupLabelEntity,
	ListAssignmentSourcesParams,
	ListAssignmentSourcesResponse
} from 'Services/Api/AssignmentSources/Types';
import ACTIVITIES_API from 'Services/Api/Activities/Api';
import {
	Activity,
	ActivityDetailResponse,
	ListActivitiesParams,
	ListActivitiesResponse,
	LmsInstanceActivity,
	ListLmsInstanceActivitiesParams,
	ListLmsInstanceActivitiesResponse,
	UpdateActivityPayload,
	UpdateActivityResponse,
	ActivityType,
	ListActivityTypesParams,
	ListActivityTypesResponse
} from 'Services/Api/Activities/Types';
import LANGUAGES_API from 'Services/Api/Languages/Api';
import {
	Language,
	ListLanguagesResponse
} from 'Services/Api/Languages/Types';
import { Status } from 'app-types';

// The scope endpoints require an offset and cap page_size at 1000, which also
// caps how many scope entities one assignment can hold.
const SCOPE_PAGE_PARAMS = { offset: 0, page_size: 1000 };

export const ASSIGNMENT_SOURCES_PAGE_SIZE = 25;

type RawScopeEntity = Record<string, string | number | undefined>;

const scopeEntityId = (entity: RawScopeEntity) =>
	entity.id ?? entity.role_id ?? entity.group_id ?? entity.user_id;

// Courses are served by the activities endpoint on the Training Manager API;
// this adapts the Activity shape to the Course shape used across the LMS UI.
const adaptActivityToCourse = (activity: Activity): Course => ({
	...activity,
	lms_mappings: (activity.lms_instance_mappings ?? []).map((mapping) => ({
		lms_instance_id: mapping.lms_instance_id,
		lms_course_id: mapping.lms_activity_id
	}))
});

class LearningManagerStore {
	status = {
		createLearningPath: Status.Idle,
		updateLearningPath: Status.Idle,
		deleteLearningPath: Status.Idle,
		getLearningPath: Status.Idle,
		listLearningPaths: Status.Idle,
		createAssignmentSource: Status.Idle,
		updateAssignmentSource: Status.Idle,
		deleteAssignmentSource: Status.Idle,
		resetAssignmentSource: Status.Idle,
		resetAssignmentSourceUsers: Status.Idle,
		unassignAssignmentSourceUsers: Status.Idle,
		getAssignmentSource: Status.Idle,
		getAssignmentSourceScopes: Status.Idle,
		listAssignmentSources: Status.Idle,
		loadMoreAssignmentSources: Status.Idle,
		listActivities: Status.Idle,
		loadAllCourses: Status.Idle,
		listLmsInstanceActivities: Status.Idle,
		listActivityTypes: Status.Idle,
		listLanguages: Status.Idle,
		getActivity: Status.Idle,
		updateActivity: Status.Idle
	};

	learningPaths: LearningPath[] = [];
	learningPathsTotal = 0;
	// Tenant each cached list was loaded for, so callers can tell a warm list
	// from one belonging to a different tenant. Not observable — bookkeeping.
	learningPathsTenantId: number | undefined = undefined;
	learningPathsSetupType: LearningPathSetupType | undefined = undefined;
	activitiesTenantId: number | undefined = undefined;
	currentLearningPath: LearningPath | null = null;
	coursesById: Record<number, Course> = {};
	learningPathsById: Record<number, LearningPath> = {};
	assignmentSources: AssignmentSource[] = [];
	assignmentSourcesTotal = 0;
	isAssignmentSourcesExhausted = false;
	assignmentSourcesRequestId = 0;
	activities: Activity[] = [];
	activitiesTotal = 0;
	allCoursesTotal = 0;
	// The activity currently being edited in the course-mapping form.
	currentActivity: Activity | null = null;
	// LMS activities available for course mapping, across all LMS instances.
	lmsInstanceActivities: LmsInstanceActivity[] = [];
	// Options for the course type dropdown.
	activityTypes: ActivityType[] = [];
	languages: Language[] = [];
	private languagesRequest: Promise<void> | null = null;
	// In-flight bulk course load, so repeat/concurrent callers share one fetch
	// instead of each re-loading the catalog. Not observable — coordination only.
	allCoursesPromise: Promise<void> | null = null;
	// The tenant the cached catalog was loaded for, so switching tenants
	// reloads it instead of serving the previous tenant's courses.
	allCoursesTenantId: number | undefined = undefined;

	constructor() {
		makeObservable(this, {
			status: observable,
			learningPaths: observable,
			learningPathsTotal: observable,
			currentLearningPath: observable,
			coursesById: observable,
			learningPathsById: observable,
			assignmentSources: observable,
			assignmentSourcesTotal: observable,
			isAssignmentSourcesExhausted: observable,
			activities: observable,
			activitiesTotal: observable,
			allCoursesTotal: observable,
			currentActivity: observable,
			lmsInstanceActivities: observable,
			activityTypes: observable,
			languages: observable,
			languagesById: computed,
			lmsInstanceActivitiesByInstanceId: computed,
			activityTypesById: computed
		});
	}

	// Activity types keyed by id, for O(1) name lookups (e.g. showing a course's
	// type in the details modal).
	get activityTypesById(): Record<number, ActivityType> {
		return this.activityTypes.reduce<Record<number, ActivityType>>(
			(acc, type) => {
				acc[type.id] = type;
				return acc;
			},
			{}
		);
	}

	get languagesById(): Record<number, Language> {
		return this.languages.reduce<Record<number, Language>>(
			(acc, language) => {
				acc[language.id] = language;
				return acc;
			},
			{}
		);
	}

	// Groups the LMS activities by their LMS instance id, so the course-mapping
	// UI can list the selectable activities for each instance.
	get lmsInstanceActivitiesByInstanceId(): Record<
		number,
		LmsInstanceActivity[]
	> {
		return this.lmsInstanceActivities.reduce<
			Record<number, LmsInstanceActivity[]>
		>((acc, activity) => {
			if (!acc[activity.lms_instance_id]) {
				acc[activity.lms_instance_id] = [];
			}
			acc[activity.lms_instance_id].push(activity);
			return acc;
		}, {});
	}

	@action
	async createLearningPath(payload: LearningPathPayload): Promise<number> {
		runInAction(() => {
			this.status.createLearningPath = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.post<LearningPathIdResponse>(
				LEARNING_MANAGER_API.LEARNING_PATHS,
				payload
			);
			runInAction(() => {
				this.status.createLearningPath = Status.Success;
			});
			return data.data.id;
		} catch (e) {
			runInAction(() => {
				this.status.createLearningPath = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async updateLearningPath(
		id: number,
		payload: LearningPathPayload
	): Promise<void> {
		runInAction(() => {
			this.status.updateLearningPath = Status.Pending;
		});
		try {
			await lmsAxiosClient.put<LearningPathIdResponse>(
				LEARNING_MANAGER_API.LEARNING_PATH(id),
				payload
			);
			runInAction(() => {
				this.status.updateLearningPath = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.updateLearningPath = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async deleteLearningPath(id: number): Promise<void> {
		runInAction(() => {
			this.status.deleteLearningPath = Status.Pending;
		});
		try {
			await lmsAxiosClient.delete<LearningPathIdResponse>(
				LEARNING_MANAGER_API.LEARNING_PATH(id)
			);
			runInAction(() => {
				this.learningPaths = this.learningPaths.filter(
					(lp) => lp.id !== id
				);
				this.status.deleteLearningPath = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.deleteLearningPath = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async getLearningPath(id: number): Promise<void> {
		runInAction(() => {
			this.status.getLearningPath = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.get<LearningPathDetailResponse>(
				LEARNING_MANAGER_API.LEARNING_PATH(id)
			);
			runInAction(() => {
				this.currentLearningPath = data.data;
				this.status.getLearningPath = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.getLearningPath = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async listLearningPaths(
		params: ListLearningPathsParams = { offset: 0 }
	): Promise<void> {
		runInAction(() => {
			this.status.listLearningPaths = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.get<ListLearningPathsResponse>(
				LEARNING_MANAGER_API.LEARNING_PATHS,
				{ params }
			);
			runInAction(() => {
				this.learningPaths = data.data;
				this.learningPathsTotal = data.meta.total;
				this.learningPathsTenantId = params.tenant_id;
				this.learningPathsSetupType = params.setup_type;
				this.status.listLearningPaths = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listLearningPaths = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchCourseById(id: number): Promise<void> {
		try {
			const { data } = await lmsAxiosClient.get<ActivityDetailResponse>(
				ACTIVITIES_API.ACTIVITY(id)
			);
			runInAction(() => {
				this.coursesById = {
					...this.coursesById,
					[id]: adaptActivityToCourse(data.data)
				};
			});
		} catch (e) {
			throw e;
		}
	}

	/**
	 * Loads the full activity catalog into `coursesById` in one bulk paginated
	 * request instead of one `/activities/:id` call per course. Deduped: repeat
	 * or concurrent callers (Quick Setup, Course Assignments) share a single
	 * load. Call this, then read `coursesById[id]` for any referenced course.
	 *
	 * `tenantId` limits the catalog to the courses that tenant can be assigned
	 * — passed by the create-assignment flow when a super admin has picked one.
	 */
	@action
	async fetchAllCourses(pageSize = 100, tenantId?: number): Promise<void> {
		if (this.allCoursesPromise && this.allCoursesTenantId === tenantId) {
			return this.allCoursesPromise;
		}
		// A different tenant means a different catalog, so drop the cached one
		// rather than merging two tenants' courses into the same map.
		if (this.allCoursesTenantId !== tenantId) {
			runInAction(() => {
				this.coursesById = {};
			});
		}
		this.allCoursesTenantId = tenantId;
		this.allCoursesPromise = (async () => {
			runInAction(() => {
				this.status.loadAllCourses = Status.Pending;
			});
			try {
				let offset = 0;
				let total = Infinity;
				const courses: Course[] = [];
				// Page through in case the catalog exceeds one page-size.
				while (offset < total) {
					const {
						data
					} = await lmsAxiosClient.get<ListActivitiesResponse>(
						ACTIVITIES_API.ACTIVITIES,
						{
							params: {
								offset,
								page_size: pageSize,
								...(tenantId ? { tenant_id: tenantId } : {})
							}
						}
					);
					total = data.meta.total;
					if (!data.data.length) break;
					courses.push(...data.data.map(adaptActivityToCourse));
					offset += data.data.length;
				}
				runInAction(() => {
					const merged = { ...this.coursesById };
					courses.forEach((course) => {
						merged[course.id] = course;
					});
					this.coursesById = merged;
					this.allCoursesTotal =
						total === Infinity ? courses.length : total;
					this.activitiesTotal = this.allCoursesTotal;
					this.status.loadAllCourses = Status.Success;
				});
			} catch (e) {
				runInAction(() => {
					this.status.loadAllCourses = Status.Failure;
				});
				// Allow a retry on the next call after a failure.
				this.allCoursesPromise = null;
				throw e;
			}
		})();
		return this.allCoursesPromise;
	}

	@action
	async createAssignmentSource(
		payload: AssignmentSourcePayload
	): Promise<number> {
		runInAction(() => {
			this.status.createAssignmentSource = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.post<AssignmentSourceIdResponse>(
				ASSIGNMENT_SOURCES_API.ASSIGNMENT_SOURCES,
				payload
			);
			runInAction(() => {
				this.status.createAssignmentSource = Status.Success;
			});
			return data.data.id;
		} catch (e) {
			runInAction(() => {
				this.status.createAssignmentSource = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async listAssignmentSources(
		params: ListAssignmentSourcesParams = { offset: 0 },
		{ silent = false }: { silent?: boolean } = {}
	): Promise<void> {
		const requestId = (this.assignmentSourcesRequestId += 1);
		if (!silent) {
			runInAction(() => {
				this.status.listAssignmentSources = Status.Pending;
			});
		}
		try {
			const { data } = await lmsAxiosClient.get<
				ListAssignmentSourcesResponse
			>(ASSIGNMENT_SOURCES_API.ASSIGNMENT_SOURCES, {
				params: {
					...params,
					page_size: params.page_size ?? ASSIGNMENT_SOURCES_PAGE_SIZE
				}
			});
			if (requestId !== this.assignmentSourcesRequestId) return;
			runInAction(() => {
				this.assignmentSources = data.data;
				this.assignmentSourcesTotal = data.meta.total;
				this.isAssignmentSourcesExhausted = false;
				this.status.listAssignmentSources = Status.Success;
				this.status.loadMoreAssignmentSources = Status.Idle;
			});
		} catch (e) {
			if (requestId !== this.assignmentSourcesRequestId) throw e;
			runInAction(() => {
				this.status.listAssignmentSources = Status.Failure;
			});
			throw e;
		}
	}

	get hasMoreAssignmentSources(): boolean {
		return (
			!this.isAssignmentSourcesExhausted &&
			this.assignmentSources.length < this.assignmentSourcesTotal
		);
	}

	@action
	async loadMoreAssignmentSources(
		params: ListAssignmentSourcesParams
	): Promise<void> {
		if (
			!this.hasMoreAssignmentSources ||
			this.status.listAssignmentSources === Status.Pending ||
			this.status.loadMoreAssignmentSources === Status.Pending
		) {
			return;
		}
		const requestId = this.assignmentSourcesRequestId;
		runInAction(() => {
			this.status.loadMoreAssignmentSources = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.get<
				ListAssignmentSourcesResponse
			>(ASSIGNMENT_SOURCES_API.ASSIGNMENT_SOURCES, {
				params: {
					...params,
					offset: this.assignmentSources.length,
					page_size: ASSIGNMENT_SOURCES_PAGE_SIZE
				}
			});
			if (requestId !== this.assignmentSourcesRequestId) return;
			runInAction(() => {
				const knownIds = new Set(this.assignmentSources.map((s) => s.id));
				this.assignmentSources = [
					...this.assignmentSources,
					...data.data.filter((s) => !knownIds.has(s.id))
				];
				this.assignmentSourcesTotal = data.meta.total;
				this.isAssignmentSourcesExhausted = !data.data.length;
				this.status.loadMoreAssignmentSources = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.assignmentSourcesRequestId) return;
			runInAction(() => {
				this.status.loadMoreAssignmentSources = Status.Failure;
			});
		}
	}

	@action
	async fetchAllLearningPaths(tenantId?: number): Promise<void> {
		let offset = 0;
		let total = Infinity;
		const learningPaths: LearningPath[] = [];
		while (offset < total) {
			const { data } = await lmsAxiosClient.get<ListLearningPathsResponse>(
				LEARNING_MANAGER_API.LEARNING_PATHS,
				{
					params: {
						offset,
						...(tenantId ? { tenant_id: tenantId } : {})
					}
				}
			);
			total = data.meta.total;
			if (!data.data.length) break;
			learningPaths.push(...data.data);
			offset += data.data.length;
		}
		runInAction(() => {
			this.learningPathsById = learningPaths.reduce<
				Record<number, LearningPath>
			>((acc, learningPath) => {
				acc[learningPath.id] = learningPath;
				return acc;
			}, {});
		});
	}

	@action
	async updateAssignmentSource(
		id: number,
		payload:
			| AssignmentSourceUpdatePayload
			| AssignmentSourceLimitedUpdatePayload
	): Promise<void> {
		runInAction(() => {
			this.status.updateAssignmentSource = Status.Pending;
		});
		try {
			await lmsAxiosClient.put<AssignmentSourceIdResponse>(
				`${ASSIGNMENT_SOURCES_API.ASSIGNMENT_SOURCE(id)}`,
				payload
			);
			runInAction(() => {
				this.status.updateAssignmentSource = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.updateAssignmentSource = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async deleteAssignmentSource(id: number): Promise<void> {
		runInAction(() => {
			this.status.deleteAssignmentSource = Status.Pending;
		});
		try {
			await lmsAxiosClient.delete<AssignmentSourceIdResponse>(
				ASSIGNMENT_SOURCES_API.ASSIGNMENT_SOURCE(id)
			);
			runInAction(() => {
				const remaining = this.assignmentSources.filter(
					(s) => s.id !== id
				);
				if (remaining.length < this.assignmentSources.length) {
					this.assignmentSourcesTotal = Math.max(
						0,
						this.assignmentSourcesTotal - 1
					);
				}
				this.assignmentSources = remaining;
				this.status.deleteAssignmentSource = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.deleteAssignmentSource = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async resetAssignmentSource(id: number): Promise<void> {
		runInAction(() => {
			this.status.resetAssignmentSource = Status.Pending;
		});
		try {
			await lmsAxiosClient.post<AssignmentSourceIdResponse>(
				ASSIGNMENT_SOURCES_API.RESET(id)
			);
			runInAction(() => {
				this.status.resetAssignmentSource = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.resetAssignmentSource = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async unAssignCourse(id: number, payload: any): Promise<void> {
		try {
			const url = `${ASSIGNMENT_SOURCES_API.UNASSIGN_COURSES}/${id}`;
			await lmsAxiosClient.post(url, payload);
		} catch (e) {
			throw e;
		}
	}

	@action
	async resetAssignmentSourceUsers(
		id: number,
		userIds: string[],
		tenantId: number | undefined
	): Promise<void> {
		runInAction(() => {
			this.status.resetAssignmentSourceUsers = Status.Pending;
		});
		try {
			const payload: AssignmentSourceUsersPayload = { user_ids: userIds, ...(tenantId ? { tenant_id : tenantId } : {}) };
			await lmsAxiosClient.post<AssignmentSourceIdResponse>(
				ASSIGNMENT_SOURCES_API.RESET_USERS(id),
				payload
			);
			runInAction(() => {
				this.status.resetAssignmentSourceUsers = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.resetAssignmentSourceUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async unassignAssignmentSourceUsers(
		id: number,
		userIds: string[],
		tenantId: number | undefined
	): Promise<void> {
		runInAction(() => {
			this.status.unassignAssignmentSourceUsers = Status.Pending;
		});
		try {
			const payload: AssignmentSourceUsersPayload = { user_ids: userIds, ...(tenantId ? { tenant_id : tenantId } : {}) };
			await lmsAxiosClient.post<AssignmentSourceIdResponse>(
				ASSIGNMENT_SOURCES_API.UNASSIGN_USERS(id),
				payload
			);
			runInAction(() => {
				this.status.unassignAssignmentSourceUsers = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.unassignAssignmentSourceUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async getAssignmentSource(id: number): Promise<AssignmentSource> {
		runInAction(() => {
			this.status.getAssignmentSource = Status.Pending;
		});
		try {
			const { data } = await throttledRequest(() =>
				lmsAxiosClient.get<AssignmentSourceDetailResponse>(
					ASSIGNMENT_SOURCES_API.ASSIGNMENT_SOURCE(id)
				)
			);
			runInAction(() => {
				this.status.getAssignmentSource = Status.Success;
			});
			return data.data;
		} catch (e) {
			runInAction(() => {
				this.status.getAssignmentSource = Status.Failure;
			});
			throw e;
		}
	}

	private async fetchScopeEntities<T = AssignmentScopeEntity>(
		url: string
	): Promise<T[]> {
		runInAction(() => {
			this.status.getAssignmentSourceScopes = Status.Pending;
		});
		try {
			const { data } = await throttledRequest(() =>
				lmsAxiosClient.get<{ data: T[] }>(url, {
					params: SCOPE_PAGE_PARAMS
				})
			);
			runInAction(() => {
				this.status.getAssignmentSourceScopes = Status.Success;
			});
			return data?.data ?? [];
		} catch (e) {
			runInAction(() => {
				this.status.getAssignmentSourceScopes = Status.Failure;
			});
			throw e;
		}
	}

	private async fetchNumericScopeIds(
		url: string
	): Promise<AssignmentScopeEntity[]> {
		const entities = await this.fetchScopeEntities<RawScopeEntity>(url);
		return entities
			.map((entity) => ({ id: Number(scopeEntityId(entity)) }))
			.filter((entity) => Number.isFinite(entity.id));
	}

	@action
	getAssignmentSourceScopeRoles(id: number): Promise<AssignmentScopeEntity[]> {
		return this.fetchNumericScopeIds(ASSIGNMENT_SOURCES_API.SCOPE_ROLES(id));
	}

	@action
	getAssignmentSourceScopeGroups(
		id: number
	): Promise<AssignmentScopeEntity[]> {
		return this.fetchNumericScopeIds(ASSIGNMENT_SOURCES_API.SCOPE_GROUPS(id));
	}

	@action
	getAssignmentSourceScopeGroupLabels(
		id: number
	): Promise<AssignmentScopeGroupLabelEntity[]> {
		return this.fetchScopeEntities<AssignmentScopeGroupLabelEntity>(
			ASSIGNMENT_SOURCES_API.SCOPE_GROUP_LABELS(id)
		);
	}

	@action
	async getAssignmentSourceScopeUsers(
		id: number
	): Promise<AssignmentScopeUserEntity[]> {
		runInAction(() => {
			this.status.getAssignmentSourceScopes = Status.Pending;
		});
		try {
			const { data } = await throttledRequest(() =>
				lmsAxiosClient.get<{ data: RawScopeEntity[] }>(
					ASSIGNMENT_SOURCES_API.SCOPE_USERS(id),
					{ params: SCOPE_PAGE_PARAMS }
				)
			);
			runInAction(() => {
				this.status.getAssignmentSourceScopes = Status.Success;
			});
			return (data?.data ?? [])
				.map(scopeEntityId)
				.filter((userId) => userId !== undefined && userId !== '')
				.map((userId) => ({ id: String(userId) }));
		} catch (e) {
			runInAction(() => {
				this.status.getAssignmentSourceScopes = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async listActivities(
		params: ListActivitiesParams = { offset: 0 }
	): Promise<void> {
		runInAction(() => {
			this.status.listActivities = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.get<ListActivitiesResponse>(
				ACTIVITIES_API.ACTIVITIES,
				{ params }
			);
			runInAction(() => {
				this.activities = data.data;
				this.activitiesTotal = data.meta.total;
				this.activitiesTenantId = params.tenant_id;
				this.status.listActivities = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listActivities = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchLmsInstanceActivities(
		params: ListLmsInstanceActivitiesParams = { offset: 1, page_size: 1000 }
	): Promise<void> {
		runInAction(() => {
			this.status.listLmsInstanceActivities = Status.Pending;
		});
		try {
			const {
				data
			} = await lmsAxiosClient.get<ListLmsInstanceActivitiesResponse>(
				ACTIVITIES_API.LMS_INSTANCE_ACTIVITIES,
				{ params }
			);
			runInAction(() => {
				this.lmsInstanceActivities = data.data;
				this.status.listLmsInstanceActivities = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listLmsInstanceActivities = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchActivityTypes(
		params: ListActivityTypesParams = { offset: 0, page_size: 1000 }
	): Promise<void> {
		runInAction(() => {
			this.status.listActivityTypes = Status.Pending;
		});
		try {
			const {
				data
			} = await lmsAxiosClient.get<ListActivityTypesResponse>(
				ACTIVITIES_API.ACTIVITY_CATEGORIES,
				{ params }
			);
			runInAction(() => {
				this.activityTypes = data.data;
				this.status.listActivityTypes = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listActivityTypes = Status.Failure;
			});
			throw e;
		}
	}

	fetchLanguages(): Promise<void> {
		if (this.status.listLanguages === Status.Success) {
			return Promise.resolve();
		}
		if (!this.languagesRequest) {
			this.languagesRequest = this.loadLanguages().finally(() => {
				this.languagesRequest = null;
			});
		}
		return this.languagesRequest;
	}

	@action
	private async loadLanguages(): Promise<void> {
		runInAction(() => {
			this.status.listLanguages = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.get<ListLanguagesResponse>(
				LANGUAGES_API.LANGUAGES
			);
			runInAction(() => {
				this.languages = data.data;
				this.status.listLanguages = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.listLanguages = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async getActivity(id: number): Promise<Activity> {
		runInAction(() => {
			this.status.getActivity = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.get<ActivityDetailResponse>(
				ACTIVITIES_API.ACTIVITY(id)
			);
			runInAction(() => {
				this.currentActivity = data.data;
				this.status.getActivity = Status.Success;
			});
			return data.data;
		} catch (e) {
			runInAction(() => {
				this.status.getActivity = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async updateActivity(
		id: number,
		payload: UpdateActivityPayload
	): Promise<number> {
		runInAction(() => {
			this.status.updateActivity = Status.Pending;
		});
		try {
			const { data } = await lmsAxiosClient.put<UpdateActivityResponse>(
				ACTIVITIES_API.ACTIVITY(id),
				payload
			);
			runInAction(() => {
				this.status.updateActivity = Status.Success;
			});
			return data.data.id;
		} catch (e) {
			runInAction(() => {
				this.status.updateActivity = Status.Failure;
			});
			throw e;
		}
	}
}

export const learningManagerStore = new LearningManagerStore();
export default LearningManagerStore;
